import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Divider, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { QualificationPromptPlaceholders } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  productGroup: ProductGroups;
  promptPlaceholders: QualificationPromptPlaceholders;
  prompt: string;
  selectedOptions: string[];

  handleAiBriefingDetailsChange(type: keyof QualificationPromptPlaceholders): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleAiBriefingDetailsChangeForLeadBooster(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectedOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const AiSearchBriefing: React.FC<Props> = ({
  productGroup,
  promptPlaceholders,
  prompt,
  selectedOptions,
  handleAiBriefingDetailsChange,
  handleAiBriefingDetailsChangeForLeadBooster,
  handleSelectedOptionChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const checkboxOptions = [
    { label: getLocalizedText('topCandidates', productGroup, t), value: 'A' },
    { label: getLocalizedText('strongContenders', productGroup, t), value: 'B' },
    { label: getLocalizedText('backupCandidatePool', productGroup, t), value: 'C' },
  ];

  return (
    <Stack>
      <Stack sx={{ mb: '16px' }}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <ArrowRightAltIcon className={classes.arrow} />
          <Typography className={classes.subTitle}>{t('refineSearchTitle')}</Typography>
        </Stack>
        <Typography className={classes.greyText} sx={{ padding: '0 8px' }}>
          {t('refineSearchDescription')}
        </Typography>
      </Stack>

      <Divider sx={{ mb: '16px' }} />

      {productGroup === ProductGroups.HR_BOOSTER ? (
        <>
          <Stack sx={{ mb: '16px' }}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <ArrowRightAltIcon className={classes.arrow} />
              <Typography className={classes.subTitle}>{getLocalizedText('qualifyLeadsQuestion', productGroup, t)}</Typography>
            </Stack>
            <Typography className={classes.greyText} sx={{ padding: '0 8px' }}>
              {getLocalizedText('qualifyLeadsDescription', productGroup, t)}
            </Typography>
          </Stack>

          <Stack sx={{ padding: '24px 0 8px 0' }}>
            <Stack gap={2}>
              <PaiTextArea
                label={t('mustHaveCriteria')}
                boldLabel={true}
                placeholder={t('mustHaveCriteriaPlaceholder')}
                value={promptPlaceholders['required']}
                variant={'background'}
                rows={5}
                onChange={handleAiBriefingDetailsChange('required')}
              />
              <PaiTextArea
                label={t('niceToHaveCriteria')}
                boldLabel={true}
                placeholder={t('niceToHaveCriteriaPlaceholder')}
                value={promptPlaceholders['preferred']}
                variant={'background'}
                rows={5}
                onChange={handleAiBriefingDetailsChange('preferred')}
              />
              <PaiTextArea
                label={t('excludeCriteria')}
                boldLabel={true}
                placeholder={t('excludeCriteriaPlaceholder')}
                value={promptPlaceholders['disqualifying']}
                variant={'background'}
                rows={5}
                onChange={handleAiBriefingDetailsChange('disqualifying')}
              />
            </Stack>
          </Stack>
        </>
      ) : null}

      {productGroup === ProductGroups.LEAD_BOOSTER ? (
        <Stack gap={1}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.label}>{`${t('leadBoosterIcpTitle')} *`}</Typography>
            <PaiHelpIcon tooltipText={t('leadBoosterIcpExplanation')} />
          </Stack>
          <Typography className={classes.greyText} mb={'8px'}>
            {t('leadBoosterIcpDescription')}
          </Typography>
          <PaiTextArea
            rows={7}
            boldLabel={true}
            placeholder={t('leadBoosterIcpPlaceholder')}
            variant={'background'}
            value={prompt}
            onChange={handleAiBriefingDetailsChangeForLeadBooster}
          />
        </Stack>
      ) : null}

      <Stack>
        <Stack sx={{ padding: '16px 0', borderBottom: (theme) => `1px solid ${theme.palette.greys.light}` }}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <ArrowRightAltIcon className={classes.arrow} />
            <Typography className={classes.subTitle}>{t('contactsToAddTitle')}</Typography>
          </Stack>
          <Typography className={classes.greyText} sx={{ padding: '0 8px' }}>
            {t('contactsToAddDescription')}
          </Typography>
        </Stack>
        <Stack sx={{ padding: '16px 24px' }}>
          <Stack gap={1}>
            {checkboxOptions.map((option) => (
              <PaiCheckbox
                key={option.value}
                value={option.value}
                label={option.label}
                checked={_.includes(selectedOptions, option.value)}
                onChange={handleSelectedOptionChange}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  arrow: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  label: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default AiSearchBriefing;
